/**
 * Example DocumentEditor using in-memory state management
 * 
 * This demonstrates the RFC vision where:
 * 1. In-memory state is the source of truth
 * 2. UI updates immediately from in-memory changes
 * 3. Sync changes flow through in-memory state
 * 4. Database is only used for loading and periodic saving
 */

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentState } from '../hooks/useDocumentState';
import { useSQLiteService } from '../hooks/useSQLiteService';
import { usePartialDeltaSync } from '../hooks/usePartialDeltaSync';
import { SQLiteService } from '../services/SQLiteService';
import { serverUrl } from '../config/environment';
import { deleteDocumentCompletely } from '../utils/documentManagement';
// SyncActivitiesDropdown removed - using inline dropdown now

interface DocumentEditorWithInMemoryStateProps {
  documentId: string;
  enableSync?: boolean; // Whether to enable sync functionality
  isRemote?: boolean; // Whether document exists on remote
  sqliteService?: SQLiteService; // Shared SQLite service instance
  onSyncStatusChange?: (isRemote: boolean) => void; // Callback to notify parent of sync status changes
}

export const DocumentEditorWithInMemoryState: React.FC<DocumentEditorWithInMemoryStateProps> = ({ 
  documentId,
  enableSync = false,
  isRemote = false,
  sqliteService: passedSqliteService,
  onSyncStatusChange
}) => {
  const navigate = useNavigate();
  // Use passed SQLite service if available, otherwise create new one
  const internalSqliteService = useSQLiteService(passedSqliteService ? undefined : documentId);
  const sqliteService = passedSqliteService || internalSqliteService;
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  const [showSyncDropdown, setShowSyncDropdown] = useState(false);
  const lastSyncTimeRef = useRef<number>(0);
  const syncDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [pendingText, setPendingText] = useState<{ [blockId: string]: string }>({});
  const textareaRefs = useRef<{ [blockId: string]: HTMLTextAreaElement }>({});
  const pageRefs = useRef<{ [pageId: string]: HTMLDivElement }>({});
  const mainContentRef = useRef<HTMLDivElement>(null);
  
  const {
    documentState,
    pages,
    isLoading,
    error,
    loadDocument,
    // applySyncChanges removed - handled by SimpleSyncEngine
    updateProperty,
    createPage,
    createTextBlock,
    saveDocument,
    getTextBlocks,
    manager,
    sourceOfTruth
  } = useDocumentState({
    documentId,
    sqliteService,
    onSyncChanges: (properties) => {
      // UI automatically updates because state changed
    },
    onContentSaved: useCallback((objectId: string, changedProperties: string[]) => {
      // Log the content save - sync will be handled separately
    }, []) // No dependencies needed for logging
  });

  // Initialize RFC sync engine with dependency injection
  // sourceOfTruth will be null initially, then available after useDocumentState initializes
  const partialDeltaSyncResult = usePartialDeltaSync(
    documentId, 
    sqliteService!,
    sourceOfTruth, // Proper dependency injection
    serverUrl,
    manager ? { 
      markClean: manager.markClean.bind(manager),
      refreshState: manager.refreshState.bind(manager)
    } : null // Pass state manager with both markClean and refreshState methods
  );
  const { 
    syncEngine: partialDeltaSyncEngine, 
    syncState, 
    sync, 
    fetchSubtreeIfNeeded,
    loadDocument: loadDocumentFromSync,
    loadPageSubtreeIfNeeded,
    checkDocumentExists,
    deleteDocumentFromRemote
  } = partialDeltaSyncResult;

  // Load document when component mounts and services are ready
  useEffect(() => {
    if (sqliteService && documentId && !isLoading && !documentState.title && !error) {
      // First check if document exists remotely using lightweight endpoint
      checkDocumentExists().then(exists => {
        if (exists) {
          console.log(`📄 Document ${documentId} exists remotely, loading...`);
          return loadDocument();
        } else {
          console.log(`📄 Document ${documentId} does not exist remotely, loading from local only...`);
          return loadDocument();
        }
      }).catch(error => {
        console.error(`❌ Failed to load document ${documentId}:`, error);
      });
    }
  }, [sqliteService, documentId, isLoading, documentState.title, error, loadDocument, checkDocumentExists]);
  
  // Debug: Log document state changes
  useEffect(() => {
    // Document state updated - UI automatically updates
  }, [documentState]);

  // Trigger sync when document state changes (after content is saved)
  useEffect(() => {
    if (enableSync && sqliteService && manager && documentState.isDirty && !syncState.isStructuralSyncing) {
      const now = Date.now();
      const timeSinceLastSync = now - lastSyncTimeRef.current;
      
      // Prevent sync if one happened recently (within 2 seconds)
      if (timeSinceLastSync < 2000) {
        return;
      }
      
      
      // Clear any existing timeout
      if (syncDebounceTimeoutRef.current) {
        clearTimeout(syncDebounceTimeoutRef.current);
      }
      
      // Use debounced sync with robust checks
      syncDebounceTimeoutRef.current = setTimeout(() => {
        // Triple-check conditions before syncing
        if (documentState.isDirty && !syncState.isStructuralSyncing && enableSync) {
          lastSyncTimeRef.current = Date.now();
          sync().catch(error => 
            console.error('Sync failed:', error)
          );
        } else {
        }
        syncDebounceTimeoutRef.current = null;
      }, 1000); // 1 second debounce
      
      return () => {
        if (syncDebounceTimeoutRef.current) {
          clearTimeout(syncDebounceTimeoutRef.current);
          syncDebounceTimeoutRef.current = null;
        }
      };
    }
  }, [enableSync, sqliteService, manager, documentState.isDirty, syncState.isStructuralSyncing, sync]);

  // Track which pages are currently being processed to prevent duplicate triggers
  const processingPagesRef = useRef<Set<string>>(new Set());

  // Set up intersection observer for lazy loading page content
  useEffect(() => {
    if (pages.length === 0 || !loadPageSubtreeIfNeeded) return;
    
    // Don't setup lazy loading during structural sync
    if (syncState?.isStructuralSyncing) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const pageElement = entry.target as HTMLElement;
            const pageId = pageElement.dataset.pageId;
            if (pageId && !processingPagesRef.current.has(pageId)) {
              
              // Mark as processing to prevent duplicate triggers
              processingPagesRef.current.add(pageId);
              
              // Trigger lazy loading for this page (RFC implementation)
              loadPageSubtreeIfNeeded(pageId).then((result) => {
                if (result.alreadyLoaded) {
                } else {
                  
                  // Trigger UI refresh after loading properties into memory
                  if (result.loadedFromLocal > 0 || result.loadedFromRemote > 0) {
                    if (manager?.refreshState) {
                      manager.refreshState();
                      
                      // Debug: Check what text blocks are now in memory for this page
                      const textBlocks = getTextBlocks(pageId);
                    } else {
                      console.warn(`⚠️ [PAGE VISIBILITY] No manager.refreshState available for page ${pageId}`);
                    }
                  }
                  
                  // Note: Removed test block creation since real text blocks exist in database
                }
              }).catch((error) => {
                console.error(`❌ [PAGE VISIBILITY] Failed to load page ${pageId}:`, error);
              }).finally(() => {
                // Remove from processing set when done
                processingPagesRef.current.delete(pageId);
              });
              
              // TODO: Load adjacent pages for better UX
            }
          }
        });
      },
      {
        root: mainContentRef.current,
        rootMargin: '100px', // Start loading 100px before page enters view
        threshold: 0.1
      }
    );

    // Observe all page elements
    const pageElements = Object.values(pageRefs.current).filter(Boolean);
    pageElements.forEach((pageElement, index) => {
      const pageId = pageElement.dataset.pageId;
      observer.observe(pageElement);
    });

    return () => {
      observer.disconnect();
    };
  }, [pages.length, loadPageSubtreeIfNeeded, syncState?.isStructuralSyncing]); // Wait for structural sync to complete

  // Trigger initial sync when document is loaded and sync is enabled
  useEffect(() => {
    if (!sqliteService || !enableSync || !manager || !documentState.isLoaded) return;

    // Add a small delay to ensure all components are initialized
    const timer = setTimeout(() => {
      sync().catch(error => 
        console.error('Initial sync failed:', error)
      );
    }, 100);

    return () => clearTimeout(timer);
  }, [documentId, enableSync, sqliteService, manager, documentState.isLoaded, sync]); // Include all dependencies

  // Load document on mount
  useEffect(() => {
    if (sqliteService && !documentState.isLoaded) {
      loadDocument();
    }
  }, [sqliteService, documentState.isLoaded, loadDocument]);

  // Set current page when pages load
  useEffect(() => {
    if (pages.length > 0 && !currentPageId) {
      setCurrentPageId(pages[0].object_id);
    }
  }, [pages, currentPageId]);

  // Use ref to track current page ID to avoid stale closures
  const currentPageIdRef = useRef(currentPageId);
  currentPageIdRef.current = currentPageId;

  // Throttle scroll events
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Flag to prevent auto-selection during manual navigation
  const isManualNavigationRef = useRef(false);

  // Scroll detection for auto-selecting page in sidebar
  const handleScroll = useCallback(() => {
    // Throttle scroll events
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
    if (!mainContentRef.current || pages.length === 0) {
      return;
    }
    
    if (isManualNavigationRef.current) {
      return;
    }

    // Use window scroll position since that's where the events are firing
    const containerHeight = window.innerHeight;
    
    let closestPageId = pages[0].object_id;
    let closestDistance = Infinity;

    pages.forEach(page => {
      const pageElement = pageRefs.current[page.object_id];
      if (pageElement) {
        const rect = pageElement.getBoundingClientRect();
        
        // Calculate distance from the top of the viewport
        const distanceFromTop = Math.abs(rect.top);
        
        // Check if page is visible in viewport (using rect.top for viewport-relative position)
        const isVisible = rect.top < containerHeight && rect.bottom > 0;
        
        if (isVisible && distanceFromTop < closestDistance) {
          closestDistance = distanceFromTop;
          closestPageId = page.object_id;
        }
      }
    });

    if (closestPageId !== currentPageIdRef.current) {
      setCurrentPageId(closestPageId);
    }
    }, 100); // 100ms throttle
  }, [pages, setCurrentPageId]);

  useEffect(() => {
    if (mainContentRef.current) {
      // Use window scroll since that's where the events are firing
      window.addEventListener('scroll', handleScroll);
      
      // Also trigger on initial load
      setTimeout(handleScroll, 100); // Small delay to ensure refs are set
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, [handleScroll]);

  // Scroll to page when selected from sidebar
  const scrollToPage = useCallback((pageId: string) => {
    const pageElement = pageRefs.current[pageId];
    if (pageElement && mainContentRef.current) {
      // Set manual navigation flag to prevent auto-selection during scroll
      isManualNavigationRef.current = true;
      
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Clear the flag after scroll animation completes (smooth scroll takes ~500ms)
      setTimeout(() => {
        isManualNavigationRef.current = false;
      }, 600);
    }
    setCurrentPageId(pageId);
  }, []);

  // Handle local edits
  const handleTitleEdit = useCallback((objectId: string, newTitle: string) => {
    updateProperty(objectId, 'title', newTitle);
    // UI updates immediately because in-memory state changed
  }, [updateProperty]);

  const handleCreatePage = useCallback(async () => {
    try {
      const pageId = await createPage('New Page');
      setCurrentPageId(pageId);
      // UI updates immediately because in-memory state changed
    } catch (error) {
      console.error('Failed to create page:', error);
    }
  }, [createPage]);

  const handleCreateTextBlock = useCallback(async (pageId: string, afterBlockId?: string) => {
    try {
      // Create the text block first (it will be added at the end by default)
      const blockId = await createTextBlock(pageId, '');
      
      // If we need to position it between blocks, update its position
      if (afterBlockId) {
        const currentBlocks = getTextBlocks(pageId);
        const currentIndex = currentBlocks.findIndex(b => b.object_id === afterBlockId);
        
        if (currentIndex !== -1) {
          const currentBlock = currentBlocks[currentIndex];
          const nextBlock = currentBlocks[currentIndex + 1];
          
          let newPosition: string;
          if (nextBlock && nextBlock.object_id !== blockId) {
            // Insert between current and next
            const currentPos = parseFloat(currentBlock.properties.get('position')?.value || '1');
            const nextPos = parseFloat(nextBlock.properties.get('position')?.value || '2');
            newPosition = ((currentPos + nextPos) / 2).toString();
          } else {
            // Insert after current
            const currentPos = parseFloat(currentBlock.properties.get('position')?.value || '1');
            newPosition = (currentPos + 0.5).toString();
          }
          
          // Update the position of the new block
          updateProperty(blockId, 'position', newPosition);
        }
      }
      
      // Focus the new block after a short delay to ensure it's rendered
      setTimeout(() => {
        const textarea = textareaRefs.current[blockId];
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(0, 0);
        }
      }, 100);
      
      return blockId;
    } catch (error) {
      console.error('Failed to create text block:', error);
      return null;
    }
  }, [createTextBlock, getTextBlocks, updateProperty]);

  const handleDeleteTextBlock = useCallback(async (blockId: string) => {
    try {
      // Tombstone the text block using in-memory state management
      updateProperty(blockId, 'object.tombstoned', true);
      
      // The UI will automatically update because the in-memory state changed
      // and getTextBlocks filters out tombstoned blocks
    } catch (error) {
      console.error('Failed to delete text block:', error);
    }
  }, [updateProperty]);

  const handleTextEdit = useCallback((blockId: string, newText: string) => {
    // Store pending text locally
    setPendingText(prev => ({ ...prev, [blockId]: newText }));
  }, []);

  const handleTextSave = useCallback((blockId: string) => {
    const text = pendingText[blockId];
    if (text !== undefined) {
      updateProperty(blockId, 'text', text);
      // Clear pending text
      setPendingText(prev => {
        const newPending = { ...prev };
        delete newPending[blockId];
        return newPending;
      });
    }
  }, [pendingText, updateProperty]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, blockId: string, pageId: string) => {
    const textarea = e.target as HTMLTextAreaElement;
    const currentText = textarea.value;
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Only create new block if current text is not empty
      if (currentText.trim() !== '') {
        // Save current text
        handleTextSave(blockId);
        // Create new text block after current one
        handleCreateTextBlock(pageId, blockId);
      }
    } else if (e.key === 'Backspace') {
      // Only delete block if text is empty AND cursor is at the beginning
      if (currentText === '' && textarea.selectionStart === 0) {
        const textBlocks = getTextBlocks(pageId);
        // Don't delete if it's the only text block on the page
        if (textBlocks.length > 1) {
          e.preventDefault();
          
          // Focus the previous block before deleting
          const currentIndex = textBlocks.findIndex(b => b.object_id === blockId);
          if (currentIndex > 0) {
            const prevBlock = textBlocks[currentIndex - 1];
            const prevTextarea = textareaRefs.current[prevBlock.object_id];
            if (prevTextarea) {
              prevTextarea.focus();
              // Position cursor at the end of the previous block
              const textLength = prevTextarea.value.length;
              prevTextarea.setSelectionRange(textLength, textLength);
            }
          }
          
          handleDeleteTextBlock(blockId);
        }
      }
    }
  }, [handleTextSave, handleCreateTextBlock, handleDeleteTextBlock, getTextBlocks]);

  // Force save (e.g., on app backgrounding)
  const handleForceSave = useCallback(async () => {
    try {
      await saveDocument();
    } catch (error) {
      console.error('❌ Failed to save document:', error);
      // Show user-friendly error message
      alert(`Failed to save document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [saveDocument]);

  const handleOffloadDocument = useCallback(async () => {
    const confirmed = window.confirm(
      `Offload "${documentId}" from this device?\n\nThis will remove the local copy but keep it in the cloud. You can download it again later.`
    );
    
    if (!confirmed) return;

    try {
      const success = await SQLiteService.deleteDocumentDatabase(documentId);
      
      if (success) {
        alert(`Document "${documentId}" has been offloaded from this device.`);
        // Navigate back to home since local copy is gone
        window.location.href = '/';
      } else {
        alert(`Failed to offload document: ${documentId}`);
      }
    } catch (error) {
      console.error(`Error offloading document ${documentId}:`, error);
      alert(`Error offloading document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [documentId]);

  const handleDeletePage = useCallback(async (pageId: string, pageTitle: string) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this page?\n\nPage: "${pageTitle}"\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      // Tombstone the page using in-memory state management
      updateProperty(pageId, 'object.tombstoned', true);
      
      // If the deleted page was the current page, switch to another page
      if (currentPageId === pageId) {
        const remainingPages = pages.filter(p => p.object_id !== pageId);
        if (remainingPages.length > 0) {
          setCurrentPageId(remainingPages[0].object_id);
        } else {
          setCurrentPageId(null);
        }
      }
      
    } catch (error) {
      console.error('Failed to delete page:', error);
      alert('Failed to delete page. Please try again.');
    }
  }, [updateProperty, currentPageId, pages]);

  const handleDeleteDocumentCompletely = useCallback(async () => {
    const confirmed = window.confirm(
      `⚠️ PERMANENTLY DELETE "${documentId}"?\n\nThis will delete the document from BOTH your device AND the cloud.\n\n🚨 THIS ACTION CANNOT BE UNDONE! 🚨`
    );
    
    if (!confirmed) return;

    // Additional confirmation step
    const confirmText = prompt(
      `To permanently delete "${documentId}", type "DELETE" exactly (all caps):`
    );
    
    if (confirmText !== 'DELETE') {
      alert('Deletion cancelled. You must type "DELETE" exactly to confirm.');
      return;
    }

    try {
      const result = await deleteDocumentCompletely(documentId, deleteDocumentFromRemote);
      
      let message = `Document "${documentId}" deletion completed:\n`;
      message += `• Local: ${result.localDeleted ? '✅ Deleted' : '❌ Failed'}\n`;
      message += `• Cloud: ${result.cloudDeleted ? '✅ Deleted' : '❌ Failed'}`;
      
      if (result.warnings && result.warnings.length > 0) {
        message += `\n\n⚠️ Warnings:\n${result.warnings.join('\n')}`;
      }
      
      if (result.errors.length > 0) {
        message += `\n\n❌ Errors:\n${result.errors.join('\n')}`;
      }
      
      alert(message);
      
      if (result.localDeleted || result.cloudDeleted) {
        // Navigate back to home since document is deleted
        window.location.href = '/';
      }
    } catch (error) {
      console.error(`Error deleting document ${documentId}:`, error);
      alert(`Error deleting document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [documentId]);

  // Early return after all hooks are called - this is now safe
  if (!sqliteService) {
    return (
      <div className="document-editor-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Initializing document database...</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="document-editor-loading">
        <div className="loading-spinner" />
        <p>Loading document from database into memory...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="document-editor-error">
        <p>Error: {error}</p>
        <button onClick={loadDocument}>Retry</button>
      </div>
    );
  }

  return (
    <div className="notion-document-editor">
      {/* Structural Sync Overlay */}
      {syncState?.isStructuralSyncing && (
        <div className="structural-sync-overlay">
          <div className="structural-sync-content">
            <div className="structural-sync-spinner">🏗️</div>
            <h3>Syncing Document Structure</h3>
            <p>Please wait while we sync the document and pages...</p>
          </div>
        </div>
      )}

      {/* Document Header */}
      <header className="document-header">
        <div className="header-left">
          <button onClick={() => navigate('/')} className="back-btn">
            <span className="back-arrow">⟨</span>
            <span>Home</span>
          </button>
        </div>
        
        <div className="document-title-section">
          <div className="sync-status-icon" title={isRemote ? 'Synced with cloud' : 'Local only'}>
            {isRemote ? (
              <span className="cloud-emoji">☁️</span>
            ) : (
              <span className="no-cloud-emoji">📱</span>
            )}
          </div>
          <input
            type="text"
            value={documentState.title}
            onChange={(e) => handleTitleEdit(documentId, e.target.value)}
            placeholder="Untitled Document"
            className="document-title-input"
          />
        </div>
        
        <div className="header-right">
          
          <div className="status-group">
            {documentState.isDirty ? (
              <button 
                onClick={handleForceSave}
                className="save-now-btn"
                title="Click to save changes"
              >
                Save now
              </button>
            ) : (
              <span className="status-indicator saved">
                Saved
              </span>
            )}
            
            {/* Combined Sync Status & Action Button */}
            <span className="sync-status" style={{ position: 'relative' }}>
              {enableSync ? (
                // Server available - show sync status or clickable sync button
                syncState.isStructuralSyncing ? (
                  <span className="status-indicator syncing" title="Syncing document structure">
                    🏗️ Syncing structure...
                  </span>
                ) : syncState.isFetchingSubtree || syncState.syncInProgress ? (
                  <span className="status-indicator syncing" title="Syncing document">
                    🔄 Syncing
                  </span>
                ) : (
                  <button
                    className="sync-action-btn synced"
                    onClick={() => {
                      sync().catch(error => 
                        console.error('Manual sync failed:', error)
                      );
                    }}
                    title="Document synced - Click to sync again"
                  >
                    ☁️ Synced
                  </button>
                )
              ) : isRemote ? (
                // Server was detected but sync disabled - show offline with retry action
                <button
                  className="sync-action-btn offline"
                  onClick={() => {
                    sync().catch(error => 
                      console.error('Manual sync failed:', error)
                    );
                  }}
                  disabled={syncState.isStructuralSyncing || syncState.isFetchingSubtree || syncState.syncInProgress}
                  title="Server unavailable - Click to retry connection"
                >
                  📡 Retry sync
                </button>
              ) : (
                // No server detected - show local only with push action
                <button
                  className="sync-action-btn local-only"
                  onClick={() => {
                    sync().then(result => {
                      // If we successfully pushed properties, the document is now remote
                      if (result && result.pushedCount > 0) {
                        onSyncStatusChange?.(true); // Notify parent that document is now remote
                      }
                    }).catch(error => 
                      console.error('Manual sync failed:', error)
                    );
                  }}
                  disabled={syncState.isStructuralSyncing || syncState.isFetchingSubtree || syncState.syncInProgress}
                  title="Push local document to cloud - Will attempt to connect to server"
                >
                  💾 Push to cloud
                </button>
              )}
            </span>

            {/* Context Menu for Sync Actions */}
            {showSyncDropdown && (
                  <div className="sync-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    minWidth: '200px'
                  }}>
                    <button 
                      onClick={() => {
                        sync();
                        setShowSyncDropdown(false);
                      }}
                      style={{ display: 'block', width: '100%', padding: '4px 8px', margin: '2px 0', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                    >
                      🔄 Sync Document
                    </button>
                    <button 
                      onClick={() => {
                        loadDocumentFromSync({ loadFromDisk: false, partialLoad: false });
                        setShowSyncDropdown(false);
                      }}
                      style={{ display: 'block', width: '100%', padding: '4px 8px', margin: '2px 0', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                    >
                      📥 Download All
                    </button>
                    {currentPageId && (
                      <button 
                        onClick={() => {
                          fetchSubtreeIfNeeded(currentPageId);
                          setShowSyncDropdown(false);
                        }}
                        style={{ display: 'block', width: '100%', padding: '4px 8px', margin: '2px 0', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                      >
                        🔍 Load Page Content
                      </button>
                    )}
                  </div>
                )}
            
            <span className="last-saved">
              {documentState.lastModified ? new Date(documentState.lastModified).toLocaleTimeString() : 'Not saved'}
            </span>
          </div>
          
          <div className="context-menu">
            <button className="context-menu-btn" title="More actions">
              <svg width="16" height="16" viewBox="0 0 16 16">
                <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
                <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
              </svg>
            </button>
            <div className="context-menu-dropdown">
              <button onClick={handleOffloadDocument}>
                🗂️ Offload
              </button>
              <button onClick={handleDeleteDocumentCompletely} className="danger">
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>
      </header>
      <div className="editor-layout">
        {/* Sidebar */}
        <aside className="pages-sidebar">
          <div className="sidebar-header">
            <h3>Pages</h3>
            <button onClick={handleCreatePage} className="add-page-btn" title="Add new page">
              <span className="add-icon">+</span>
            </button>
          </div>
          
          <div className="pages-list">
            {pages.length === 0 ? (
              <div className="empty-sidebar">
                <p>No pages yet</p>
                <button onClick={handleCreatePage} className="create-first-page-btn">
                  Create first page
                </button>
              </div>
            ) : (
              pages.map((page) => (
                <div 
                  key={page.object_id}
                  className={`page-item ${currentPageId === page.object_id ? 'active' : ''}`}
                  onClick={() => scrollToPage(page.object_id)}
                >
                  <div className="page-item-content">
                    <span className="page-title">{page.title || 'Untitled'}</span>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePage(page.object_id, page.title || 'Untitled Page');
                      }}
                      className="page-delete-btn"
                      title="Delete page"
                    >
                      <span className="delete-icon">×</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="page-content" ref={mainContentRef}>
          {pages.length === 0 ? (
            <div className="empty-document-state">
              <div className="empty-icon">📄</div>
              <h3>Start writing your first page</h3>
              <p>Click "Add Page" to create your first page.</p>
            </div>
          ) : (
            <div className="pages-scroll-container">
              {pages.map((page, pageIndex) => (
                <div 
                  key={page.object_id} 
                  className="scrollable-page"
                  data-page-id={page.object_id}
                  ref={(el) => {
                    if (el) pageRefs.current[page.object_id] = el;
                  }}
                >
                  {/* Page Title */}
                  <div className="page-title-section">
                    <input
                      type="text"
                      value={page.title || 'Untitled'}
                      onChange={(e) => handleTitleEdit(page.object_id, e.target.value)}
                      placeholder="Untitled"
                      className="page-title-input"
                    />
                  </div>

                  {/* Text Blocks */}
                  <div className="text-blocks">
                    {getTextBlocks(page.object_id).length === 0 ? (
                      <div 
                        className="empty-page-state clickable"
                        onClick={() => handleCreateTextBlock(page.object_id)}
                      >
                        <p>Click here to start writing...</p>
                      </div>
                    ) : (
                      getTextBlocks(page.object_id).map((block) => {
                        const savedText = block.content || '';
                        const displayText = pendingText[block.object_id] !== undefined 
                          ? pendingText[block.object_id] 
                          : savedText;
                        
                        return (
                          <div key={block.object_id} className="text-block-wrapper">
                            <textarea
                              ref={(el) => {
                                if (el) textareaRefs.current[block.object_id] = el;
                              }}
                              value={displayText}
                              onChange={(e) => handleTextEdit(block.object_id, e.target.value)}
                              onBlur={() => handleTextSave(block.object_id)}
                              onKeyDown={(e) => handleKeyDown(e, block.object_id, page.object_id)}
                              placeholder="Start writing..."
                              className="text-block"
                              rows={1}
                              onInput={(e) => {
                                // Auto-resize textarea
                                const target = e.target as HTMLTextAreaElement;
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                              }}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>
                  
                  {/* Page separator - except for last page */}
                  {pageIndex < pages.length - 1 && (
                    <div className="page-separator"></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>


      {/* Debug Info */}
      <div className="debug-info">
        <details>
          <summary>Debug: Document State</summary>
          <pre>{JSON.stringify({
            documentId,
            objectCount: documentState.objects.size,
            pageCount: pages.length,
            currentPageId,
            pendingTextBlocks: Object.keys(pendingText).length,
            isDirty: documentState.isDirty,
            isLoaded: documentState.isLoaded,
            lastModified: documentState.lastModified
          }, null, 2)}</pre>
        </details>
        
        <details>
          <summary>Debug: Pages ({pages.length})</summary>
          <pre>{JSON.stringify(pages.map(page => ({
            id: page.object_id,
            title: page.title || 'Untitled',
            textBlockCount: getTextBlocks(page.object_id).length,
            isActive: page.object_id === currentPageId,
            textBlocks: getTextBlocks(page.object_id).map(block => ({
              id: block.object_id,
              text: (block.properties.get('text')?.value || '').substring(0, 30) + '...',
              position: block.properties.get('position')?.value
            }))
          })), null, 2)}</pre>
        </details>
      </div>

      {/* Modern Notion-like CSS with Sidebar */}
      <style>{`
        /* Modern Notion-like Document Editor */
        .notion-document-editor {
          min-height: 100vh;
          background: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #37352f;
          line-height: 1.5;
          display: flex;
          flex-direction: column;
        }

        /* Document Header */
        .document-header {
          position: sticky;
          top: 0;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid #e9e9e7;
          padding: 8px 24px;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          z-index: 100;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .back-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 8px;
          background: transparent;
          border: none;
          border-radius: 4px;
          color: #2383e2;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.15s ease;
        }
        
        .back-arrow {
          font-size: 16px;
          font-weight: normal;
        }

        .back-btn:hover {
          background: rgba(35, 131, 226, 0.08);
        }

        .push-to-cloud-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 8px;
          background: transparent;
          border: 1px solid #e9e9e7;
          border-radius: 4px;
          color: #37352f;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          transition: all 0.15s ease;
        }
        
        .push-to-cloud-btn:hover {
          background: rgba(35, 131, 226, 0.08);
          border-color: #2383e2;
          color: #2383e2;
        }
        
        .push-to-cloud-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .sync-action-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 8px;
          background: transparent;
          border: 1px solid #e9e9e7;
          border-radius: 4px;
          color: #37352f;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          transition: all 0.15s ease;
        }
        
        .sync-action-btn:hover:not(:disabled) {
          background: rgba(35, 131, 226, 0.08);
          border-color: #2383e2;
          color: #2383e2;
        }
        
        .sync-action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .sync-action-btn.synced {
          border-color: #10b981;
          color: #10b981;
        }
        
        .sync-action-btn.synced:hover {
          background: rgba(16, 185, 129, 0.08);
          border-color: #059669;
          color: #059669;
        }
        
        .sync-action-btn.offline {
          border-color: #f59e0b;
          color: #f59e0b;
        }
        
        .sync-action-btn.local-only {
          border-color: #6366f1;
          color: #6366f1;
        }
        
        .upload-icon {
          font-size: 14px;
        }

        .sync-status-icon {
          display: flex;
          align-items: center;
          color: #9b9a97;
          transition: color 0.15s ease;
        }

        .cloud-emoji, .no-cloud-emoji {
          font-size: 32px;
        }

        .document-title-section {
          flex: 1;
          min-width: 0;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .document-title-input {
          width: 100%;
          font-size: 24px;
          font-weight: 700;
          color: #37352f;
          border: none;
          outline: none;
          background: transparent;
          padding: 6px 8px;
          border-radius: 3px;
          transition: background-color 0.15s ease;
        }

        .document-title-input::placeholder {
          color: #9b9a97;
        }

        .document-title-input:focus {
          background-color: rgba(35, 131, 226, 0.03);
        }

        .document-actions {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .status-group {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
        }

        .status-indicator {
          padding: 2px 6px;
          border-radius: 3px;
          font-weight: 500;
        }

        .status-indicator.saved {
          color: #0f7b0f;
          background-color: #e7f3e7;
        }

        .save-now-btn {
          padding: 2px 6px;
          border-radius: 3px;
          font-weight: 500;
          background-color: #fef3cd;
          color: #b7791f;
          border: none;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.15s ease;
        }

        .save-now-btn:hover {
          background-color: #fde68a;
          color: #92400e;
        }

        /* Sync Progress Overlay */
        .sync-progress-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(4px);
          border-bottom: 1px solid #e9e9e7;
          padding: 8px 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
        }

        .sync-progress-content {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #37352f;
        }

        .sync-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #e9e9e7;
          border-top: 2px solid #2383e2;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .last-saved {
          color: #9b9a97;
          font-size: 11px;
        }

        .context-menu {
          position: relative;
        }

        .context-menu-btn {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 4px;
          color: #9b9a97;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .context-menu-btn:hover {
          background: rgba(155, 154, 151, 0.1);
          color: #37352f;
        }

        .context-menu-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          border: 1px solid #e9e9e7;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          padding: 4px 0;
          min-width: 140px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-4px);
          transition: all 0.15s ease;
          z-index: 1000;
        }

        .context-menu:hover .context-menu-dropdown {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .context-menu-dropdown button {
          width: 100%;
          padding: 8px 12px;
          background: none;
          border: none;
          text-align: left;
          font-size: 14px;
          color: #37352f;
          cursor: pointer;
          transition: background-color 0.15s ease;
        }

        .context-menu-dropdown button:hover {
          background: #f7f6f3;
        }

        .context-menu-dropdown button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .context-menu-dropdown button.danger {
          color: #eb5757;
        }

        .context-menu-dropdown button.danger:hover {
          background: rgba(235, 87, 87, 0.1);
        }

        /* Editor Layout */
        .editor-layout {
          display: flex;
          height: calc(100vh - 57px);
          gap: 0;
        }

        /* Sidebar */
        .pages-sidebar {
          width: 280px;
          background: #f7f6f3;
          border-right: 1px solid #e9e9e7;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .sidebar-header {
          padding: 16px 20px 12px;
          border-bottom: 1px solid #e9e9e7;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .sidebar-header h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #37352f;
        }

        .add-page-btn {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 4px;
          color: #9b9a97;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        
        .add-icon {
          font-size: 22px;
          font-weight: 300;
          line-height: 1;
        }

        .add-page-btn:hover {
          background: rgba(35, 131, 226, 0.08);
          color: #2383e2;
        }

        .pages-list {
          flex: 1;
          overflow-y: auto;
          padding: 8px 0;
        }

        .empty-sidebar {
          padding: 40px 20px;
          text-align: center;
          color: #9b9a97;
        }

        .empty-sidebar p {
          margin: 0 0 16px;
          font-size: 14px;
        }

        .create-first-page-btn {
          padding: 8px 16px;
          background: #2383e2;
          color: white;
          border: none;
          border-radius: 3px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.15s ease;
        }

        .create-first-page-btn:hover {
          background: #1a6ec2;
        }

        .page-item {
          display: flex;
          align-items: center;
          padding: 8px 20px;
          cursor: pointer;
          transition: background-color 0.15s ease;
          border-left: 3px solid transparent;
        }

        .page-item:hover {
          background: rgba(35, 131, 226, 0.03);
        }

        .page-item.active {
          background: rgba(35, 131, 226, 0.08);
          border-left-color: #2383e2;
        }

        .page-item-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          gap: 8px;
        }

        .page-title {
          flex: 1;
          font-size: 14px;
          color: #37352f;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .page-delete-btn {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 3px;
          color: #9b9a97;
          cursor: pointer;
          opacity: 0;
          transition: all 0.15s ease;
        }
        
        .delete-icon {
          font-size: 20px;
          font-weight: 300;
          line-height: 1;
        }

        .page-item:hover .page-delete-btn {
          opacity: 1;
        }

        .page-delete-btn:hover {
          background: rgba(235, 87, 87, 0.1);
          color: #eb5757;
        }

        /* Main Content */
        .page-content {
          flex: 1;
          overflow-y: auto;
          padding: 0;
        }

        .empty-document-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          color: #9b9a97;
          padding: 40px;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
          opacity: 0.6;
        }

        .empty-document-state h3 {
          font-size: 16px;
          font-weight: 500;
          margin: 0 0 8px;
          color: #37352f;
        }

        .empty-document-state p {
          font-size: 14px;
          margin: 0;
          color: #9b9a97;
        }

        /* Scrollable Pages */
        .pages-scroll-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 60px;
        }

        .scrollable-page {
          padding: 40px 0;
          position: relative;
        }

        .page-separator {
          height: 1px;
          background: #e9e9e7;
          margin: 40px -60px;
          position: relative;
        }

        .page-separator::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 20px;
          height: 1px;
          background: #9b9a97;
        }

        .page-title-section {
          margin-bottom: 24px;
        }

        .page-title-input {
          width: 100%;
          font-size: 28px;
          font-weight: 700;
          line-height: 1.2;
          color: #37352f;
          border: none;
          outline: none;
          background: transparent;
          padding: 3px 2px;
          margin: 0;
          font-family: inherit;
        }

        .page-title-input::placeholder {
          color: #9b9a97;
        }

        .page-title-input:focus {
          background-color: rgba(35, 131, 226, 0.03);
          border-radius: 3px;
        }

        /* Text Blocks */
        .text-blocks {
          position: relative;
        }

        .empty-page-state {
          text-align: center;
          padding: 60px 0;
          color: #9b9a97;
          font-style: italic;
        }

        .empty-page-state.clickable {
          cursor: pointer;
          border-radius: 6px;
          transition: background-color 0.15s ease;
        }

        .empty-page-state.clickable:hover {
          background-color: rgba(35, 131, 226, 0.03);
        }

        .empty-page-state p {
          margin: 0;
          font-size: 14px;
        }

        .text-block-wrapper {
          margin: 2px 0;
          position: relative;
        }

        .text-block {
          width: 100%;
          min-height: 24px;
          padding: 3px 2px;
          border: none;
          outline: none;
          background: transparent;
          font-size: 16px;
          font-family: inherit;
          line-height: 1.5;
          color: #37352f;
          resize: none;
          overflow: hidden;
          border-radius: 3px;
          transition: background-color 0.15s ease;
        }

        .text-block::placeholder {
          color: #9b9a97;
        }

        .text-block:focus {
          background-color: rgba(35, 131, 226, 0.03);
        }

        /* Debug Info */
        .debug-info {
          position: fixed;
          bottom: 16px;
          right: 16px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          border: 1px solid #e9e9e7;
          border-radius: 6px;
          padding: 8px;
          font-size: 12px;
          color: #9b9a97;
          max-width: 350px;
          z-index: 1000;
        }

        .debug-info details {
          margin-bottom: 8px;
        }

        .debug-info details:last-child {
          margin-bottom: 0;
        }

        .debug-info summary {
          cursor: pointer;
          font-weight: 500;
          margin-bottom: 8px;
        }

        .debug-info pre {
          margin: 0;
          font-size: 11px;
          overflow: auto;
          max-height: 200px;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .pages-scroll-container {
            padding: 0 40px;
          }

          .scrollable-page {
            padding: 32px 0;
          }

          .pages-sidebar {
            width: 240px;
          }
        }

        @media (max-width: 768px) {
          .document-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
            padding: 16px;
          }

          .document-actions {
            width: 100%;
            justify-content: space-between;
          }

          .document-title-input {
            font-size: 20px;
          }

          .editor-layout {
            flex-direction: column;
          }

          .pages-sidebar {
            width: 100%;
            height: 200px;
            border-right: none;
            border-bottom: 1px solid #e9e9e7;
            position: sticky;
            top: 0;
          }

          .pages-list {
            display: flex;
            overflow-x: auto;
            overflow-y: hidden;
            padding: 8px 16px;
            gap: 8px;
          }

          .page-item {
            flex-shrink: 0;
            min-width: 120px;
            padding: 8px 12px;
            border-left: none;
            border-bottom: 3px solid transparent;
          }

          .page-item.active {
            border-left: none;
            border-bottom-color: #2383e2;
          }

          .pages-scroll-container {
            padding: 0 20px;
          }

          .scrollable-page {
            padding: 24px 0 60px;
          }

          .page-title-input {
            font-size: 24px;
          }

          .debug-info {
            position: relative;
            bottom: auto;
            right: auto;
            margin: 16px;
            max-width: none;
          }
        }

        @media (max-width: 480px) {
          .page-title-input {
            font-size: 22px;
          }

          .text-block {
            font-size: 15px;
          }
        }

        /* Structural Sync Overlay */
        .structural-sync-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(255, 255, 255, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          backdrop-filter: blur(2px);
        }

        .structural-sync-content {
          text-align: center;
          padding: 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(0, 0, 0, 0.1);
        }

        .structural-sync-spinner {
          font-size: 3rem;
          margin-bottom: 1rem;
          animation: pulse 2s ease-in-out infinite alternate;
        }

        .structural-sync-content h3 {
          margin: 0 0 0.5rem 0;
          color: #2c3e50;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .structural-sync-content p {
          margin: 0;
          color: #7f8c8d;
          font-size: 1rem;
        }

        @keyframes pulse {
          from { opacity: 0.6; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};
